// Represented v0 (Sprint 10, solo_pro+). Mirror of Portfolio.tsx with the
// broker-representation delta: representation_status, owner contact block,
// listing-agreement expiry. Verification of the listing agreement lands in
// the ownership-claims flow alongside owned-property verification.

import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  FileCheck2,
  Loader2,
  Plus,
  Upload,
  X,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Masthead } from '@/components/Masthead';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SkeletonLine } from '@/components/Skeleton';
import type {
  CreateRepresentedInput,
  RepresentationStatus,
  RepresentedProperty,
} from '@/lib/api-types';

const REPRESENTATION_STATUSES: { value: RepresentationStatus; label: string }[] = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'engaged', label: 'Engaged' },
  { value: 'signed', label: 'Signed' },
  { value: 'expired', label: 'Expired' },
  { value: 'terminated', label: 'Terminated' },
];

const DOC_TYPES: { value: string; label: string }[] = [
  { value: 'title', label: 'Title / Deed' },
  { value: 'ALTA_survey', label: 'ALTA Survey' },
  { value: 'ADA', label: 'ADA Assessment' },
  { value: 'environmental_phase_I', label: 'Environmental Phase I' },
  { value: 'floor_plan', label: 'Floor Plan' },
  { value: 'photos', label: 'Photos' },
  { value: 'energy_audit', label: 'Energy Audit' },
  { value: 'rent_roll', label: 'Rent Roll' },
  { value: 'certificate_of_occupancy', label: 'Certificate of Occupancy' },
  { value: 'historic_status', label: 'Historic Status' },
  { value: 'other', label: 'Other' },
];

function formatBytes(n: number | null): string {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(ms: number | null): string | null {
  if (!ms) return null;
  const d = new Date(ms);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
}

function dateInputToMs(v: string): number | null {
  if (!v) return null;
  // Parse as UTC to keep round-trip deterministic regardless of local tz.
  const ms = Date.parse(`${v}T00:00:00Z`);
  return Number.isFinite(ms) ? ms : null;
}

const STATUS_STYLES: Record<RepresentationStatus, string> = {
  signed: 'border-success-border bg-success-soft text-success-bold',
  engaged: 'border-brand-500/30 bg-brand-500/10 text-brand-700',
  prospect: 'border-rule bg-surface-sunk text-ink-muted',
  expired: 'border-danger-border bg-danger-soft text-danger-bold',
  terminated: 'border-danger-border bg-danger-soft text-danger-bold',
};

function StatusBadge({ status }: { status: RepresentationStatus }) {
  const cls = STATUS_STYLES[status] ?? STATUS_STYLES.prospect;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide ${cls}`}
    >
      {status}
    </span>
  );
}

export default function Represented() {
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['represented'], queryFn: api.listRepresented });

  const gated =
    list.isError && list.error instanceof ApiError && list.error.status === 403;

  return (
    <div className="flex flex-col gap-6">
      <Masthead
        kicker="Track B"
        title="Represented"
        dateline={
          gated
            ? 'Requires Solo Pro'
            : list.data
              ? `${list.data.count} properties — ${list.data.plan} plan`
              : 'Loading…'
        }
        lede="Properties where you hold a listing agreement. Offer-ready only once the listing agreement is uploaded and verified — like Portfolio, the offer-builder pulls only from here (plus Portfolio)."
      />

      {gated && (
        <div className="rounded-xl border border-warn-border bg-warn-soft px-4 py-3 text-[0.8125rem] text-warn-bold">
          The Represented track is a Solo Pro feature. Upgrade to manage broker-represented properties and upload listing agreements.
        </div>
      )}

      {!gated && (
        <div className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[0.9375rem] font-semibold text-ink">Add a represented property</div>
            <div className="text-[0.8125rem] text-ink-muted">
              V0 trusts you on the listing agreement. Verification and owner-attestation queue lands in Sprint 10.
            </div>
          </div>
          <Button
            onClick={() => setShowAddForm((v) => !v)}
            data-testid="represented-add-button"
          >
            {showAddForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {showAddForm ? 'Cancel' : 'Add property'}
          </Button>
        </div>
      )}

      {!gated && showAddForm && (
        <AddPropertyForm onSuccess={() => setShowAddForm(false)} />
      )}

      {!gated && list.isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card flex flex-col gap-2 p-5">
              <SkeletonLine width="50%" />
              <SkeletonLine width="35%" />
            </div>
          ))}
        </div>
      ) : !gated && (!list.data || list.data.properties.length === 0) ? (
        <EmptyState
          title="No represented properties yet"
          description="Add the first one above. Each property accepts a vault of evidence docs (title, ALTA survey, ADA, etc.) plus the listing agreement."
        />
      ) : !gated && list.data ? (
        <div className="flex flex-col gap-3">
          {list.data.properties.map((p) => (
            <PropertyCard
              key={p.id}
              property={p}
              expanded={expandedId === p.id}
              onToggle={() => setExpandedId((cur) => (cur === p.id ? null : p.id))}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function AddPropertyForm({ onSuccess }: { onSuccess: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CreateRepresentedInput>({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    representation_status: 'prospect',
  });
  const [startsAtStr, setStartsAtStr] = useState('');
  const [expiresAtStr, setExpiresAtStr] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.createRepresentedProperty({
        ...form,
        listing_agreement_starts_at: dateInputToMs(startsAtStr),
        listing_agreement_expires_at: dateInputToMs(expiresAtStr),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['represented'] });
      onSuccess();
    },
  });

  const errMsg =
    create.isError && create.error instanceof ApiError ? create.error.detail : null;

  function set<K extends keyof CreateRepresentedInput>(key: K, value: CreateRepresentedInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  return (
    <form
      className="card grid gap-3 p-5 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
      data-testid="represented-add-form"
    >
      <Field label="Property name">
        <Input value={form.name} onChange={(e) => set('name', e.target.value)} required />
      </Field>
      <Field label="Representation status">
        <select
          value={form.representation_status}
          onChange={(e) => set('representation_status', e.target.value as RepresentationStatus)}
          className="h-10 w-full rounded-lg border border-rule bg-surface px-3 text-sm text-ink"
        >
          {REPRESENTATION_STATUSES.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Street address" wide>
        <Input value={form.address} onChange={(e) => set('address', e.target.value)} required />
      </Field>
      <Field label="City">
        <Input value={form.city} onChange={(e) => set('city', e.target.value)} required />
      </Field>
      <Field label="State (2-letter)">
        <Input
          value={form.state}
          onChange={(e) => set('state', e.target.value.toUpperCase().slice(0, 2))}
          maxLength={2}
          required
        />
      </Field>
      <Field label="ZIP">
        <Input value={form.zip} onChange={(e) => set('zip', e.target.value)} required />
      </Field>
      <Field label="Owner contact name">
        <Input
          value={form.owner_contact_name ?? ''}
          onChange={(e) => set('owner_contact_name', e.target.value || null)}
        />
      </Field>
      <Field label="Owner contact email">
        <Input
          type="email"
          value={form.owner_contact_email ?? ''}
          onChange={(e) => set('owner_contact_email', e.target.value || null)}
        />
      </Field>
      <Field label="Owner contact phone">
        <Input
          value={form.owner_contact_phone ?? ''}
          onChange={(e) => set('owner_contact_phone', e.target.value || null)}
        />
      </Field>
      <Field label="Listing agreement starts">
        <Input
          type="date"
          value={startsAtStr}
          onChange={(e) => setStartsAtStr(e.target.value)}
        />
      </Field>
      <Field label="Listing agreement expires">
        <Input
          type="date"
          value={expiresAtStr}
          onChange={(e) => setExpiresAtStr(e.target.value)}
        />
      </Field>
      <Field label="Rentable SF (optional)">
        <Input
          type="number"
          value={form.rentable_sf ?? ''}
          onChange={(e) => set('rentable_sf', e.target.value ? Number(e.target.value) : null)}
        />
      </Field>
      <Field label="Year built (optional)">
        <Input
          type="number"
          value={form.year_built ?? ''}
          onChange={(e) => set('year_built', e.target.value ? Number(e.target.value) : null)}
        />
      </Field>
      <div className="flex items-center justify-between sm:col-span-2">
        <div className="text-[0.75rem] text-ink-muted">
          You can attach the listing agreement and supporting docs after the property is created.
        </div>
        <div className="flex items-center gap-3">
          {errMsg && <span className="text-[0.75rem] text-danger-bold">{errMsg}</span>}
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save property
          </Button>
        </div>
      </div>
    </form>
  );
}

function Field({
  label,
  wide,
  children,
}: {
  label: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={`flex flex-col gap-1 ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="text-[0.75rem] font-medium text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

function PropertyCard({
  property,
  expanded,
  onToggle,
}: {
  property: RepresentedProperty;
  expanded: boolean;
  onToggle: () => void;
}) {
  const expiry = formatDate(property.listing_agreement_expires_at);
  const contactLine = [
    property.owner_contact_name,
    property.owner_contact_email,
    property.owner_contact_phone,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="card flex flex-col gap-3 p-5" data-testid="represented-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-[1rem] font-semibold text-ink">{property.name}</div>
            <StatusBadge status={property.representation_status} />
          </div>
          <div className="text-[0.8125rem] text-ink-muted">
            {property.address} · {property.city}, {property.state} {property.zip}
          </div>
          <div className="mt-1 text-[0.6875rem] uppercase tracking-wide text-ink-dim">
            {contactLine || 'No owner contact yet'}
            {property.rentable_sf ? ` · ${property.rentable_sf.toLocaleString()} RSF` : ''}
            {property.year_built ? ` · built ${property.year_built}` : ''}
            {expiry ? ` · Expires: ${expiry}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ReadinessBadge score={property.readiness_score} docs={property.document_count ?? 0} />
          <Button variant="secondary" onClick={onToggle}>
            {expanded ? 'Hide docs' : 'Manage docs'}
          </Button>
        </div>
      </div>
      {expanded && <DocumentsPanel propertyId={property.id} />}
    </div>
  );
}

function ReadinessBadge({ score, docs }: { score: number; docs: number }) {
  return (
    <div className="flex flex-col items-end">
      <div className="text-[1rem] font-bold tabular-nums text-ink">{score}</div>
      <div className="text-[0.625rem] uppercase tracking-wide text-ink-muted">
        readiness · {docs} docs
      </div>
    </div>
  );
}

function DocumentsPanel({ propertyId }: { propertyId: string }) {
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ['represented-detail', propertyId],
    queryFn: () => api.getRepresentedProperty(propertyId),
  });
  const [docType, setDocType] = useState('floor_plan');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function onFile(file: File) {
    setUploading(true);
    setUploadError(null);
    try {
      await api.uploadRepresentedDocument(propertyId, file, docType);
      await qc.invalidateQueries({ queryKey: ['represented-detail', propertyId] });
      await qc.invalidateQueries({ queryKey: ['represented'] });
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.detail : (err as Error).message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div className="border-t border-rule pt-3">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end">
        <Field label="Document type">
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="h-10 w-full rounded-lg border border-rule bg-surface px-3 text-sm text-ink"
          >
            {DOC_TYPES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </Field>
        <div>
          <input
            ref={fileRef}
            type="file"
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
            }}
            className="block text-sm"
            data-testid="represented-doc-upload"
          />
        </div>
        {uploading && (
          <div className="inline-flex items-center gap-1 text-[0.75rem] text-ink-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading
          </div>
        )}
      </div>
      {uploadError && (
        <div role="alert" className="mb-3 text-[0.75rem] text-danger-bold">
          Upload failed: {uploadError}
        </div>
      )}
      {detail.isLoading ? (
        <SkeletonLine />
      ) : detail.data && detail.data.documents.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {detail.data.documents.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-3 rounded-md border border-rule bg-surface-sunk px-3 py-2 text-[0.8125rem]"
            >
              <div className="flex items-center gap-2">
                <FileCheck2 className="h-4 w-4 text-brand-700" />
                <span className="font-medium text-ink">{d.original_filename}</span>
                <span className="text-[0.6875rem] uppercase tracking-wide text-ink-dim">
                  {d.doc_type.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="text-[0.6875rem] text-ink-muted">{formatBytes(d.bytes)}</div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-md border border-dashed border-rule px-3 py-4 text-[0.8125rem] text-ink-muted">
          <Upload className="mr-1 inline-block h-3.5 w-3.5" /> No documents yet — upload the listing
          agreement, title, survey, and any supporting docs the owner has shared.
        </div>
      )}
      <div className="mt-3 inline-flex items-center gap-1 text-[0.6875rem] text-ink-dim">
        <Briefcase className="h-3 w-3" /> Listing-agreement verification + AI doc classify lands in Sprint 10.
      </div>
    </div>
  );
}
